// ── CallRegister 새 등록 액션 (useCallRegisterSubmit.js 356줄을 300줄 규칙으로 나누며 글자 그대로 옮김, 2026-10-13) ──
// - applyNaver: 네이버 플레이스 텍스트 → 폼 자동 채우기
// - onPasteNaver: 붙여넣기 이벤트 처리
// - onSubmit: 신규 등록 (지오코딩 검증 + preferences 저장 포함)
// 훅이 아니라 매 렌더 useCallRegisterSubmit 안에서 불리는 함수 묶음이다(값은 그 렌더의 core·opts).

import { apiClient } from '../../../api/apiClient';
import {
  DEFAULT_FORM, ADDRESS_GEO_FAIL_MESSAGE, REQUIRED_LABELS,
} from './constants';
import {
  sanitizePhoneInput, normalizeAddressForSubmit,
  geocodeAddressForClient, parseNaverPlace,
} from './utils';

// callsUrl: 콜 쓰기 주소 — 기본 '/api/tm/calls', 통합 일일콜 메뉴는 메뉴 주소(2026-10-13 3단계 5번)
// integrated: 통합 일일콜 메뉴 — 등록할 회사(companyCode)를 꼭 골라야 한다(사용자 결정 5-a, 서버도 재확인)
export function createCallCreateActions(core, {
  adminMode, isMovieAdminMode, companyCode,
  showToast, fetchList, nextBizDate, upsertPref,
  callsUrl = '/api/tm/calls', integrated = false,
}) {
  const {
    form, setForm, setNaverText,
    isExternalWorker,
    missingRequiredFields, isSubmitReady,
    salesPersonNames, fixedSalesPerson,
    setSaving, setSubmitResultText, setListPage,
  } = core;

  // ── 네이버 플레이스 자동 채우기 ───────────────────────────────────────────
  function applyNaver(raw, toast) {
    const p = parseNaverPlace(raw);
    const updates = {};
    if (p.company_name) updates.company_name = p.company_name;
    if (p.industry) updates.industry = p.industry;
    if (p.address) updates.address = p.address;
    if (p.phone) updates.phone = sanitizePhoneInput(p.phone);
    const keys = Object.keys(updates);
    if (!keys.length) {
      if (toast) showToast('자동 추출 가능한 항목이 없습니다.', 'error');
      return;
    }
    setForm((prev) => ({ ...prev, ...updates }));
    if (toast) showToast(`자동 입력 완료 (${keys.join(', ')})`, 'success');
  }

  /** Ctrl+V 이벤트: 클립보드 텍스트를 네이버 플레이스로 파싱 후 폼 자동 채우기 */
  function onPasteNaver(e) {
    const text = e.clipboardData?.getData('text') || '';
    if (!text) return;
    e.preventDefault();
    applyNaver(text, true);
    setNaverText('');
  }

  // ── 신규 등록 ─────────────────────────────────────────────────────────────
  async function onSubmit() {
    if (integrated && !companyCode) return showToast('등록할 회사를 먼저 골라 주세요.', 'error');
    if (!isSubmitReady) {
      const names = missingRequiredFields.map((key) => REQUIRED_LABELS[key] || key).join(', ');
      return showToast(`입력 필요: ${names}`, 'error');
    }

    const salesName = String(form.sales_person_name || '').trim();
    if (adminMode && salesPersonNames.length > 0 && !salesPersonNames.includes(salesName)) {
      return showToast('마케팅담당자 이름을 다시 확인해 주세요.', 'error');
    }
    if (adminMode) {
      const ownerUserId = Number(form.owner_user_id);
      if (!Number.isInteger(ownerUserId) || ownerUserId <= 0) {
        return showToast('담당자를 선택해 주세요.', 'error');
      }
    }

    const normalizedAddress = adminMode
      ? String(form.address || '').trim()
      : normalizeAddressForSubmit(form.address);

    setSaving(true);
    let validatedCoord = null;
    if (!adminMode) {
      // 직원 등록: 주소 지오코딩 검증 필수
      const validation = await apiClient.post('/api/tm/address/validate', {
        address: normalizedAddress,
      });
      if (validation.ok) {
        validatedCoord = {
          latitude: Number(validation.data?.latitude),
          longitude: Number(validation.data?.longitude),
        };
      } else if (validation.status === 404) {
        // 서버 구버전(검증 API 미반영) → 클라이언트 Nominatim 폴백
        validatedCoord = await geocodeAddressForClient(normalizedAddress);
        if (!validatedCoord) {
          setSaving(false);
          showToast(ADDRESS_GEO_FAIL_MESSAGE, 'error');
          return;
        }
      } else {
        setSaving(false);
        showToast(validation.data?.message || ADDRESS_GEO_FAIL_MESSAGE, 'error');
        return;
      }
    }

    // 담당자 고정 설정 저장
    if (fixedSalesPerson) {
      const ok = await upsertPref(true, form.sales_person_name);
      if (!ok) { setSaving(false); return; }
    } else {
      await upsertPref(false, '');
    }

    const r = await apiClient.post(callsUrl, {
      ...form,
      address: normalizedAddress,
      owner_user_id: adminMode ? Number(form.owner_user_id) : undefined,
      phone: sanitizePhoneInput(form.phone),
      mobile_phone: sanitizePhoneInput(form.mobile_phone),
      // ticket_type_id: 상품 선택 모드 → 선택한 상품 ID
      // ticket_type: 카테고리 고정 모드 → 카테고리명 텍스트 (ticket_type_id=null과 함께)
      ticket_type_id: form.ticket_type_id || null,
      ticket_type: form.ticket_type || null,
      sales_person_name: salesName,
      latitude: validatedCoord?.latitude,
      longitude: validatedCoord?.longitude,
      // 이 콜이 어느 회사 것인지 — 지금 보고 있는 화면의 회사.
      //   메뉴·라우트가 회사별로 갈리므로(/my/tm-calls-lnj → companyCode="LNJ") 이 값이 곧 회사다.
      //   서버가 이 선언을 배정 보유 여부로 검증한 뒤 tm_calls.company_code 에 저장한다.
      //   ※ company_name(방문처 상호)과 다른 값이라 이름을 구분했다.
      call_company_code: companyCode || undefined,
    });
    setSaving(false);

    if (!r.ok) return showToast(r.data?.message || '콜 등록에 실패했습니다.', 'error');

    const next = await nextBizDate();
    const keep = fixedSalesPerson ? salesName : '';
    setForm({
      ...DEFAULT_FORM,
      owner_user_id: adminMode ? String(form.owner_user_id || '') : '',
      visit_date: next,
      sales_person_name: keep,
      // 외부직원/영화관리/companyCode: ticket_type_id, ticket_type 유지 (useEffect가 자동 재설정)
      ticket_type_id: (isExternalWorker || isMovieAdminMode || Boolean(companyCode))
        ? form.ticket_type_id : null,
      ticket_type: (isExternalWorker || isMovieAdminMode || Boolean(companyCode))
        ? form.ticket_type : '',
    });
    setNaverText('');
    setSubmitResultText('등록성공!');
    setListPage(1); // 방금 등록한 콜(최신)이 보이게 1페이지로(2026-10-23 페이지 나누기)
    fetchList();
  }

  return { applyNaver, onPasteNaver, onSubmit };
}
